/**
 * Sprint 10 Batch 1.4: Trial Expiry Warning Modal
 * Shows when trial is about to expire (last day) or has just expired
 * Offers extension (if available) or upgrade options
 */
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { 
  AlertTriangle, Clock, Zap, ArrowRight, X, 
  CalendarPlus, CheckCircle, Sparkles, Crown
} from 'lucide-react';
import { formatPrice } from '../config/packages';
import { usePackageCatalog } from '../context/PackageCatalogContext';

const TrialExpiryWarningModal = ({ 
  isOpen,
  onClose,
  trialStatus,
  onExtendTrial,
  onUpgrade,
  loading = false
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isExtending, setIsExtending] = useState(false);
  const { getPackageById } = usePackageCatalog();
  
  if (!isOpen || !trialStatus) return null;
  
  const { 
    expired, 
    daysRemaining, 
    currentDay, 
    totalDays, 
    extended,
    canExtend,
    maxExtendedDays,
    defaultDays
  } = trialStatus;
  
  // Determine modal variant
  const isLastDay = daysRemaining === 1;
  const showExtensionOption = canExtend && !expired && onExtendTrial;
  
  const handleExtend = async () => {
    setIsExtending(true);
    try {
      await onExtendTrial?.();
      onClose?.();
    } catch (error) {
      console.error('Failed to extend trial:', error);
    } finally {
      setIsExtending(false);
    }
  };
  
  const handleUpgrade = (packageId) => {
    if (onUpgrade) {
      onUpgrade(packageId);
    } else {
      navigate('/clinic/packages');
    }
    onClose?.();
  };
  
  const starterPackage = getPackageById('STARTER');
  
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className={`px-6 py-5 ${expired ? 'bg-red-500' : 'bg-amber-500'} text-white`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${expired ? 'bg-red-400' : 'bg-amber-400'}`}>
                {expired ? (
                  <AlertTriangle className="h-5 w-5" />
                ) : (
                  <Clock className="h-5 w-5" />
                )}
              </div>
              <div>
                <h2 className="text-lg font-bold">
                  {expired 
                    ? t('trialWarning.expiredTitle', 'Trial Expired')
                    : isLastDay
                      ? t('trialWarning.lastDayTitle', 'Last Day of Trial')
                      : t('trialWarning.expiringTitle', 'Trial Ending Soon')
                  }
                </h2>
                <p className="text-sm opacity-90">
                  {expired 
                    ? t('trialWarning.expiredSubtitle', 'Your free trial has ended')
                    : isLastDay
                      ? t('trialWarning.lastDaySubtitle', 'Your trial ends today')
                      : t('trialWarning.expiringSubtitle', '{{days}} day(s) remaining', { days: daysRemaining })
                  }
                </p>
              </div>
            </div>
            <button 
              onClick={onClose}
              className="p-1 hover:bg-white/20 rounded-lg transition-colors"
              data-testid="close-trial-warning"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
        
        <div className="p-6 space-y-5">
          {/* Trial Status Display */}
          <div className="flex items-center justify-center">
            <div className={`px-4 py-2 rounded-full text-sm font-medium ${
              expired 
                ? 'bg-red-100 text-red-700' 
                : 'bg-amber-100 text-amber-700'
            }`}>
              {expired 
                ? t('trialWarning.trialEnded', 'Trial ended on Day {{day}} of {{total}}', { day: totalDays, total: totalDays })
                : t('trialWarning.currentStatus', 'Day {{current}} of {{total}}', { current: currentDay, total: totalDays })
              }
            </div>
          </div>
          
          {/* Extension Option (if available) */}
          {showExtensionOption && (
            <Card className="border-2 border-purple-200 bg-purple-50">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <CalendarPlus className="h-5 w-5 text-purple-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-purple-900">
                      {t('trialWarning.extendTitle', 'Need more time?')}
                    </h3>
                    <p className="text-sm text-purple-700 mt-1">
                      {t('trialWarning.extendDesc', 'Extend your trial to {{days}} days for free', { days: maxExtendedDays })}
                    </p>
                    <Button 
                      onClick={handleExtend}
                      disabled={isExtending || loading}
                      className="mt-3 bg-purple-600 hover:bg-purple-700"
                      size="sm"
                      data-testid="extend-trial-btn"
                    >
                      {isExtending ? (
                        <span className="flex items-center gap-2">
                          <span className="animate-spin">⏳</span>
                          {t('trialWarning.extending', 'Extending...')}
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          <Sparkles className="h-4 w-4" />
                          {t('trialWarning.extendButton', 'Extend Trial (+{{days}} days)', { days: maxExtendedDays - defaultDays })}
                        </span>
                      )}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          
          {/* Already Extended Notice */}
          {extended && !expired && (
            <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
              <CheckCircle className="h-4 w-4 flex-shrink-0" />
              <span>
                {t('trialWarning.alreadyExtended', "You're on an extended trial. Consider upgrading before it ends.")}
              </span>
            </div>
          )}
          
          {/* Upgrade CTA */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Crown className="h-4 w-4 text-amber-500" />
              <span className="text-sm font-medium text-gray-700">
                {t('trialWarning.upgradePrompt', 'Upgrade for uninterrupted access')}
              </span>
            </div>
            
            <Card className="border border-gray-200 hover:border-green-300 hover:shadow-md transition-all cursor-pointer group"
              onClick={() => handleUpgrade('STARTER')}
              data-testid="upgrade-starter-card"
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                      <Zap className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">{starterPackage?.displayName || 'Starter'}</h4>
                      <p className="text-sm text-gray-500">
                        {starterPackage?.limits?.leads_per_month ?? 0} leads, {starterPackage?.limits?.sales_reps ?? 0} reps
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-gray-900">
                      {starterPackage ? formatPrice(starterPackage.pricing) : ''}
                    </div>
                    <ArrowRight className="h-4 w-4 text-gray-400 group-hover:text-green-500 group-hover:translate-x-1 transition-all" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Button 
              variant="outline"
              className="w-full"
              onClick={() => {
                navigate('/clinic/packages');
                onClose?.();
              }}
              data-testid="view-all-plans-btn"
            >
              {t('trialWarning.viewAllPlans', 'View All Plans')}
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
          
          {/* Data Preservation Notice */}
          <div className="text-center text-xs text-gray-500 pt-2 border-t">
            <CheckCircle className="h-3 w-3 inline mr-1" />
            {t('trialWarning.dataPreserved', 'Your data is safe and will be preserved')}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrialExpiryWarningModal;
